// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import React from 'react';
import './StreamComponent.css';
import * as gameliftstreamssdk from './gamelift-streams-websdk/gameliftstreams-1.1.0';
import StatsOverlay from './StatsOverlay';
import ShellQrLinkPanel from './components/ShellQrLinkPanel';
import ShellTransitionPanel, { type ShellTransitionMode } from './components/ShellTransitionPanel';
import { ShellUrlConfigService, type ShellUrlConfig } from './services/ShellUrlConfigService';
import {
    MobileControllerSessionService,
    type MobileControllerMessage,
    type MobileKeyMessage,
    type MobileMouseMessage,
    type MobileStatusMessage
} from './services/MobileControllerSessionService';
import { ParentMessageRelayService } from './services/ParentMessageRelayService';
import { DataChannelHandshakeService } from './services/DataChannelHandshakeService';
import { GameLoopEnvironmentConfig } from './services/GameLoopEnvironmentConfig';
import { GameLoopJwtTokenService } from './services/GameLoopJwtTokenService';
import { StreamApiError, StreamParseError } from './services/StreamApiErrors';

interface StreamComponentProps {
}

export enum StreamState {
    STOPPED = 1,
    LOADING,
    RUNNING,
    ERROR
}

interface StreamComponentState {
    status: StreamState;
    sgId: string;
    appId: string;
    sessionId: string;
    lastSessionId: string;
    userId: string;
    launchMessage: string;
    regions: string[];
    inputEnabled: boolean;
    isStreamStarting: boolean;
    perfStats: unknown;
    showQrPanel: boolean;
    mobileSessionId: string;
    mobileUrl: string;
    mobileConnectionState: 'connected' | 'connecting' | 'disconnected';
    mobileLogs: string[];
    securityEvents: string[];
    transitionMode: ShellTransitionMode;
    errorReason: StreamErrorReason | null;
}

type StreamStartFailureStage = 'create_session' | 'create_connection' | 'start_stream' | 'wait_for_active';

type StreamApiRequestInit = Omit<RequestInit, 'headers'>;

const STREAM_SESSION_ID_PATTERN = /^[a-zA-Z0-9-]+$/;

interface StreamStartFailureMetadata {
    readonly failureStage: StreamStartFailureStage;
    readonly errorCode: string;
    readonly errorMessage: string;
}

type StreamErrorReason =
    | 'startup_timeout'
    | 'api_error'
    | 'init_failure'
    | 'reconnect_failed';

const ERROR_TITLE_BY_REASON: Record<StreamErrorReason, string> = {
    startup_timeout: 'Unable to start stream. The stream did not start within the expected time. Please try again.',
    api_error: 'Unable to connect to game server. Please try again later.',
    init_failure: 'Unable to start stream. Please try again later.',
    reconnect_failed: 'Unable to connect to game server. Please try again later.'
};

class StreamComponent extends React.Component<StreamComponentProps, StreamComponentState> {
    gameliftstreams?: gameliftstreamssdk.GameLiftStreams;
    private readonly urlConfigService: ShellUrlConfigService;
    private readonly pressedKeys: Set<string>;
    private readonly autoTerminateTimeoutMs: number;
    private readonly shellFeaturesEnabled: boolean;
    private readonly autoStartOnMobileConnected: boolean;
    private readonly showStatsEnabled: boolean;
    private readonly mobileWebSocketEndpoint: string;
    private readonly parentMessageRelayService: ParentMessageRelayService;
    private readonly dataChannelHandshakeService: DataChannelHandshakeService;
    private mobileSessionService: MobileControllerSessionService | null;
    private disconnectTimer: number | null;
    private dataChannelHandshakeStarted: boolean;
    private readonly trackedQrScannedSessionIds: Set<string>;
    private _mobileDisconnectGraceTimer: number | null;
    private _mobileDisconnectLatched: boolean;
    private readonly _mobileDisconnectGraceMs: number;
    private _mobileDisconnectManual: boolean | null;
    private _startupAttemptId: number;
    private _startupAttemptCount: number;
    private _startupAttemptStartedAtMs: number;
    private readonly _startupTimeoutMs: number;
    private _startupTimeoutTimer: number | null;
    private urlConfig: ShellUrlConfig;
    private readonly _envConfig: GameLoopEnvironmentConfig;
    private readonly _tokenService: GameLoopJwtTokenService;
    private readonly _apiBaseUrl: string;

    constructor(props: StreamComponentProps) {
        super(props);

        this.urlConfigService = new ShellUrlConfigService();
        this.urlConfig = this.urlConfigService.parseFromWindow(window.location.search);
        this._envConfig = new GameLoopEnvironmentConfig(
            window.location.host,
            process.env.REACT_APP_APP_HOST_OVERRIDE ?? null
        );
        this._tokenService = new GameLoopJwtTokenService(this._envConfig);
        const apiBaseUrl = process.env.REACT_APP_API_ENDPOINT;
        if (!apiBaseUrl || apiBaseUrl.trim().length === 0) {
            throw new Error('REACT_APP_API_ENDPOINT must be configured for _apiBaseUrl / _buildApiUrl');
        }
        this._apiBaseUrl = apiBaseUrl;
        this.autoTerminateTimeoutMs = 10000;
        this.shellFeaturesEnabled = this.readBooleanEnv('REACT_APP_ENABLE_SHELL_FEATURES', true);
        this.autoStartOnMobileConnected = this.readBooleanEnv('REACT_APP_ENABLE_AUTO_START_ON_MOBILE_CONNECTED', true);
        this.showStatsEnabled = this.urlConfig.showStats;
        this.mobileWebSocketEndpoint = process.env.REACT_APP_SHELL_MOBILE_WS_ENDPOINT ?? 'wss://glws.velope.tv';
        this.parentMessageRelayService = new ParentMessageRelayService({
            allowedOrigins: this.getAllowedParentOrigins(),
            config: this.urlConfig,
            onSecurityError: (message: string) => this.recordSecurityEvent(message)
        });
        this.dataChannelHandshakeService = new DataChannelHandshakeService({
            onSecurityError: (message: string) => this.recordSecurityEvent(message)
        });
        this.mobileSessionService = null;
        this.disconnectTimer = null;
        this.dataChannelHandshakeStarted = false;
        this.trackedQrScannedSessionIds = new Set<string>();
        this._mobileDisconnectGraceTimer = null;
        this._mobileDisconnectLatched = false;
        this._mobileDisconnectGraceMs = 5000;
        this._mobileDisconnectManual = null;
        this._startupAttemptId = 0;
        this._startupAttemptCount = 0;
        this._startupAttemptStartedAtMs = 0;
        this._startupTimeoutMs = this.urlConfig.streamStartupTimeoutMs
            ?? ShellUrlConfigService.parseStreamStartupTimeoutMs(process.env.REACT_APP_STREAM_STARTUP_TIMEOUT_MS)
            ?? ShellUrlConfigService.DEFAULT_STREAM_STARTUP_TIMEOUT_MS;
        this._startupTimeoutTimer = null;
        this.pressedKeys = new Set<string>();

        this.state = {
            status: StreamState.STOPPED,
            sgId: this.urlConfig.streamGroupId,
            appId: this.urlConfig.applicationId,
            sessionId: '',
            lastSessionId: '',
            userId: this.urlConfig.userId,
            launchMessage: '',
            regions: [this.urlConfig.location],
            inputEnabled: false,
            isStreamStarting: false,
            perfStats: {},
            showQrPanel: false,
            mobileSessionId: '',
            mobileUrl: '',
            mobileConnectionState: 'disconnected',
            mobileLogs: [],
            securityEvents: [],
            transitionMode: 'none',
            errorReason: null
        };

        this.performanceStatsCallback = this.performanceStatsCallback.bind(this);
        this.createStreamSession = this.createStreamSession.bind(this);
        this.createStreamSessionConnection = this.createStreamSessionConnection.bind(this);
        this.closeConnection = this.closeConnection.bind(this);
        this.handleMobileMessage = this.handleMobileMessage.bind(this);
        this.handleStreamConnectionState = this.handleStreamConnectionState.bind(this);
        this.handleStreamChannelError = this.handleStreamChannelError.bind(this);
        this.handleServerDisconnect = this.handleServerDisconnect.bind(this);
        this.handleShellTerminateKey = this.handleShellTerminateKey.bind(this);
        this.recordSecurityEvent = this.recordSecurityEvent.bind(this);
        this._handleErrorRetry = this._handleErrorRetry.bind(this);
        this._handleErrorReturn = this._handleErrorReturn.bind(this);
    }

    componentDidMount(): void {
        this.resetGameLiftStreamsSDK();
        this.parentMessageRelayService.start(
            (payload: Uint8Array) => this.gameliftstreams?.sendApplicationMessage(payload) ?? false,
            () => this.state.sessionId || this.state.lastSessionId
        );
        document.addEventListener('keydown', this.handleShellTerminateKey, true);
        this.initializeShellLaunchFlow();
    }

    componentWillUnmount(): void {
        document.removeEventListener('keydown', this.handleShellTerminateKey, true);
        this.parentMessageRelayService.stop();
        this.dataChannelHandshakeService.stop();
        this.mobileSessionService?.stop();
        this.mobileSessionService = null;
        this.clearDisconnectTimer();
        this._clearStartupTimer();
        this._startupAttemptId += 1;
        this._clearMobileDisconnectGraceTimer();
        this.gameliftstreams?.close();
    }

    private readBooleanEnv(key: string, defaultValue: boolean): boolean {
        const rawValue = process.env[key];
        if (rawValue === undefined) {
            return defaultValue;
        }

        return rawValue.toLowerCase() === 'true';
    }

    private getAllowedParentOrigins(): ReadonlySet<string> {
        const values = new Set<string>();
        values.add(window.location.origin);
        values.add('https://play.gameloop.tv');
        values.add('https://gameloop-launcher.dev.gameloop.tv');
        values.add('https://gameloop-launcher.stage.gameloop.tv');
        values.add('https://gameloop-launcher.gameloop.tv');
        values.add('https://play.dev.gameloop.tv');
        values.add('https://play.stage.gameloop.tv');
        values.add('https://gameloop-mobile-controller.dev.gameloop.tv');
        values.add('https://gameloop-mobile-controller.stage.gameloop.tv');
        values.add('https://gameloop-mobile-controller.gameloop.tv');
        values.add('https://stream.dev.gameloop.tv');
        values.add('https://stream.stage.gameloop.tv');
        values.add('https://stream.gameloop.tv');

        const configured = process.env.REACT_APP_ALLOWED_PARENT_ORIGINS;
        if (configured) {
            configured
                .split(',')
                .map((value: string) => value.trim())
                .filter((value: string) => value.length > 0)
                .forEach((value: string) => values.add(value));
        }

        return values;
    }

    private handleShellTerminateKey(event: KeyboardEvent): void {
        const keyCode = event.keyCode;
        if (keyCode !== 10009 && keyCode !== 8) {
            return;
        }

        if (event.repeat) {
            return;
        }

        event.preventDefault();

        if (this.state.transitionMode === 'error') {
            this._handleErrorExit('shell_back_key');
            return;
        }

        if (this.state.status === StreamState.RUNNING || this.state.isStreamStarting) {
            this.closeConnection('shell_back_key');
            return;
        }

        this.parentMessageRelayService.notifyCloseEvent('shell_back_key');
    }

    private initializeShellLaunchFlow(): void {
        if (!this.urlConfig.hasRequiredStreamParams) {
            this.setState({ launchMessage: 'Missing required shell URL params: streamGroupId/applicationId' });
            this._setErrorMode('init_failure');
            return;
        }

        if (!this.state.userId || this.state.userId.trim().length === 0) {
            this.emitStartStreamFailed({
                failureStage: 'create_session',
                errorCode: 'missing_player_id',
                errorMessage: 'Missing playerId query parameter'
            });
            this.setState({ launchMessage: 'Missing required shell URL param: playerId' });
            this._setErrorMode('init_failure');
            return;
        }

        this.setState({
            launchMessage: this.urlConfig.hasControllerId
                ? 'controllerId detected: waiting for mobile connection'
                : 'Shell params detected: auto-starting stream'
        });

        if (!this.shellFeaturesEnabled) {
            this.setState({ launchMessage: 'Shell features disabled via kill-switch' });
            this._setErrorMode('init_failure');
            return;
        }

        if (this.urlConfig.hasControllerId) {
            this.initializeMobileControllerSession();
            return;
        }

        window.setTimeout(() => {
            void this.createStreamSession();
        }, 1000);
    }

    private initializeMobileControllerSession(): void {
        if (!this.urlConfig.glQrURL) {
            this.handleError(new Error('Missing or invalid glQrURL'), 'init_failure');
            return;
        }

        const mobileSessionId = MobileControllerSessionService.generateSessionId();
        const mobileUrl = this.buildMobileUrl(mobileSessionId);

        let qrUrlDomain: string;
        try {
            qrUrlDomain = new URL(mobileUrl).hostname;
        } catch (urlError) {
            const cause = urlError instanceof Error ? urlError : new Error(String(urlError));
            this.handleError(new Error(`Invalid mobile controller URL: ${cause.message}`));
            return;
        }

        this.trackedQrScannedSessionIds.clear();

        this.setState({
            showQrPanel: true,
            mobileSessionId,
            mobileUrl,
            mobileConnectionState: 'connecting'
        });
        this.appendMobileLog(`Session created: ${mobileSessionId}`);
        this.emitAnalyticsEvent('qr_code_presented', {
            streamSessionId: this.state.sessionId || this.state.lastSessionId,
            controllerWSSessionId: mobileSessionId,
            qrUrlDomain
        });

        this.mobileSessionService?.stop();
        this._clearMobileDisconnectGraceTimer();
        this._mobileDisconnectLatched = false;
        this.mobileSessionService = new MobileControllerSessionService({
            endpoint: this.mobileWebSocketEndpoint,
            onMessage: this.handleMobileMessage,
            onProtocolError: (message: string) => this.recordSecurityEvent(message),
            onSecurityError: (message: string) => this.recordSecurityEvent(message)
        });
        this.mobileSessionService.start(mobileSessionId);
    }

    private buildMobileUrl(sessionId: string): string {
        const base = new URL(this.urlConfig.glQrURL);
        const appUrl = new URL('https://gameloop-mobile-controller.s3.us-east-1.amazonaws.com/mobile/index.html');
        appUrl.searchParams.set('session', sessionId);
        base.searchParams.set('appURL', appUrl.toString());
        return base.toString();
    }

    private appendMobileLog(entry: string): void {
        const timestamp = new Date().toISOString();
        this.setState((prevState: StreamComponentState) => ({
            mobileLogs: [...prevState.mobileLogs, `${timestamp} ${entry}`].slice(-30)
        }));
    }

    private recordSecurityEvent(message: string): void {
        this.setState((prevState: StreamComponentState) => ({
            securityEvents: [...prevState.securityEvents, message].slice(-20)
        }));
    }

    private resetGameLiftStreamsSDK(): void {
        this.gameliftstreams = new gameliftstreamssdk.GameLiftStreams({
            videoElement: this.getVideoElement(),
            audioElement: this.getAudioElement(),
            inputConfiguration: {
                setCursor: 'visibility',
                autoPointerLock: 'fullscreen'
            },
            clientConnection: {
                connectionState: this.handleStreamConnectionState,
                channelError: this.handleStreamChannelError,
                serverDisconnect: this.handleServerDisconnect,
                applicationMessage: (message: Uint8Array) => {
                    this.dataChannelHandshakeService.onApplicationMessage(message);
                    this.parentMessageRelayService.relayApplicationMessage(message);
                },
                performanceStats: this.performanceStatsCallback
            }
        });
    }

    private getVideoElement(): HTMLVideoElement {
        return document.getElementById('StreamVideoElement') as HTMLVideoElement;
    }

    private getAudioElement(): HTMLAudioElement {
        return document.getElementById(`StreamAudioElement`) as HTMLAudioElement;
    }

    private performanceStatsCallback(perfStats: unknown): void {
        this.setState({ perfStats });
    }

    private _setErrorMode(reason: StreamErrorReason): void {
        this.setState({
            transitionMode: 'error',
            errorReason: reason,
            isStreamStarting: false,
            status: StreamState.ERROR
        });
    }

    /**
     * Sets the state for any kind of error - unwraps if error is of type StreamApiError.
     */
    private handleError(e: unknown, reason: StreamErrorReason = 'api_error'): void {
        console.log(e);
        if (e instanceof StreamApiError) {
            let data: Record<string, unknown> = {};
            try {
                data = JSON.parse(e.body || '{}') as Record<string, unknown>;
            } catch (_parseError) {
                data = { message: e.body || 'Unknown error (unparseable response)' };
            }
            console.error(`Received ${e.status} error response with payload: ${e.body}`);
            this.recordSecurityEvent(`Stream API error ${e.status}: ${data.message || 'Unknown error'}`);
        } else {
            const message = e instanceof Error ? e.message : 'Unknown error';
            this.recordSecurityEvent(`Stream error: ${message}`);
        }
        this._setErrorMode(reason);
    }

    private async _buildAuthHeaders(): Promise<Record<string, string>> {
        const token = await this._tokenService.getValidToken(this.state.userId);
        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        };
    }

    private _buildApiUrl(path: string): URL {
        try {
            return new URL(path, this._apiBaseUrl);
        } catch (_error) {
            throw new Error(`Invalid API URL for path ${path}`);
        }
    }

    private _buildStreamSessionStatusPath(streamGroupId: string, streamSessionIdentifier: string): string {
        const streamSessionId = this._extractStreamSessionId(streamSessionIdentifier);
        return `/session/${encodeURIComponent(streamGroupId)}/${encodeURIComponent(streamSessionId)}`;
    }

    private _extractStreamSessionId(streamSessionIdentifier: string): string {
        const pathSegments = streamSessionIdentifier.split('/');
        const streamSessionId = pathSegments[pathSegments.length - 1] ?? '';
        if (!STREAM_SESSION_ID_PATTERN.test(streamSessionId)) {
            throw new Error('Invalid stream session identifier');
        }

        return streamSessionId;
    }

    private async _streamApiPost(path: string, payload: Record<string, unknown>): Promise<unknown> {
        return this._streamApiRequest(path, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    }

    private async _streamApiGet(path: string): Promise<unknown> {
        return this._streamApiRequest(path, {
            method: 'GET'
        });
    }

    private async _streamApiRequest(path: string, init: StreamApiRequestInit): Promise<unknown> {
        try {
            return await this._streamApiFetch(path, init);
        } catch (error) {
            if (!(error instanceof StreamApiError) || error.status !== 401) {
                throw error;
            }

            this.recordSecurityEvent(`Stream API 401 on ${init.method ?? 'GET'} ${path}; refreshing token and retrying once`);
            this._tokenService.clear();
            return this._streamApiFetch(path, init);
        }
    }

    private async _streamApiFetch(path: string, init: StreamApiRequestInit): Promise<unknown> {
        const url = this._buildApiUrl(path);
        const headers = await this._buildAuthHeaders();
        const response = await fetch(url.toString(), {
            ...init,
            headers
        });
        return this._readResponseOrThrow(response);
    }

    private async _readResponseOrThrow(response: Response): Promise<unknown> {
        const bodyText = await response.text();
        if (!response.ok) {
            throw new StreamApiError(response.status, bodyText);
        }
        if (bodyText.length === 0) {
            return {};
        }
        try {
            return JSON.parse(bodyText);
        } catch (_error) {
            throw new StreamParseError(response.status, bodyText);
        }
    }

    private getPrimaryRegion(): string {
        return this.state.regions[0] ?? this.urlConfig.location;
    }

    private buildAnalyticsPayload(payload: Record<string, unknown>): Record<string, unknown> {
        return {
            ...payload,
            streamGroupId: this.state.sgId || this.urlConfig.streamGroupId,
            applicationId: this.state.appId || this.urlConfig.applicationId,
        };
    }

    private emitAnalyticsEvent(eventName: string, payload: Record<string, unknown>): void {
        this.parentMessageRelayService.relayAnalyticsEvent(eventName, this.buildAnalyticsPayload(payload));
    }

    private toErrorCode(error: unknown): string {
        if (error instanceof StreamApiError) {
            return `api_${error.status}`;
        }

        if (error instanceof Error) {
            if (error.message === 'missing_player_id') {
                return 'missing_player_id';
            }
            return error.name || 'error';
        }

        return 'unknown_error';
    }

    private toErrorMessage(error: unknown): string {
        if (error instanceof StreamApiError && error.body) {
            try {
                const parsedBody = JSON.parse(error.body) as Record<string, unknown>;
                const dataMessage = parsedBody.message;
                if (typeof dataMessage === 'string' && dataMessage.trim()) {
                    return dataMessage;
                }
            } catch (_jsonError) {
                // Fallback to generic handling below
            }
        }

        if (error instanceof Error) {
            return error.message || 'Unknown error';
        }

        return 'Unknown error';
    }

    private emitStartStreamFailed(metadata: StreamStartFailureMetadata): void {
        this.emitAnalyticsEvent('start_stream_failed', {
            region: this.getPrimaryRegion(),
            failureStage: metadata.failureStage,
            errorCode: metadata.errorCode,
            errorMessage: metadata.errorMessage
        });
    }

    private _startStreamStartupTimer(stage: StreamStartFailureStage = 'create_session'): void {
        this._clearStartupTimer();
        this._startupAttemptStartedAtMs = Date.now();
        this._startupTimeoutTimer = window.setTimeout(() => this._onStartupTimeout(stage), this._startupTimeoutMs);
    }

    private _clearStartupTimer(): void {
        if (this._startupTimeoutTimer === null) {
            return;
        }

        window.clearTimeout(this._startupTimeoutTimer);
        this._startupTimeoutTimer = null;
    }

    private _hasStartupTimeoutElapsed(): boolean {
        return this._startupAttemptStartedAtMs > 0 && Date.now() - this._startupAttemptStartedAtMs >= this._startupTimeoutMs;
    }

    private _onStartupTimeout(stage: StreamStartFailureStage = 'create_session'): void {
        this._clearStartupTimer();

        const failedAttemptCount = this._startupAttemptCount;
        const elapsedMs = this._startupAttemptStartedAtMs > 0
            ? Date.now() - this._startupAttemptStartedAtMs
            : this._startupTimeoutMs;
        const errorMessage = `Stream did not start within ${this._startupTimeoutMs}ms`;

        this._startupAttemptId += 1;

        this.emitAnalyticsEvent('stream_startup_timeout', {
            attemptId: failedAttemptCount,
            configuredTimeoutMs: this._startupTimeoutMs,
            elapsedMs,
            failureStage: stage,
            region: this.getPrimaryRegion()
        });
        this.emitStartStreamFailed({
            failureStage: stage,
            errorCode: 'startup_timeout',
            errorMessage
        });
        this._setErrorMode('startup_timeout');
    }

    private async createStreamSession(): Promise<void> {
        if (!this.state.sgId || !this.state.appId || !this.state.regions.length) {
            this.handleError(new Error('Required stream parameters missing.'), 'init_failure');
            return;
        }

        this.clearDisconnectTimer();
        this._clearStartupTimer();
        this._startupAttemptId += 1;
        this._startupAttemptCount += 1;
        const currentAttemptId = this._startupAttemptId;
        this._startStreamStartupTimer();
        this.setState({ isStreamStarting: true, status: StreamState.LOADING, transitionMode: 'connecting' });

        try {
            const signalRequest = await this.gameliftstreams?.generateSignalRequest();
            if (currentAttemptId !== this._startupAttemptId) {
                return;
            }

            const payload = {
                AppIdentifier: this.state.appId,
                SGIdentifier: this.state.sgId,
                UserId: this.state.userId,
                SignalRequest: signalRequest ?? '',
                Regions: this.state.regions
            };
            const data = (await this._streamApiPost('/', payload)) as { arn: string };
            if (currentAttemptId !== this._startupAttemptId) {
                return;
            }

            await this.waitForACTIVE(data.arn, this.state.sgId, currentAttemptId);
        } catch (e) {
            if (currentAttemptId !== this._startupAttemptId) {
                return;
            }

            if (this._hasStartupTimeoutElapsed()) {
                this._onStartupTimeout();
                return;
            }

            this._clearStartupTimer();
            this.emitStartStreamFailed({
                failureStage: 'create_session',
                errorCode: this.toErrorCode(e),
                errorMessage: this.toErrorMessage(e)
            });
            this.handleError(e, 'api_error');
        }
    }

    private async createStreamSessionConnection(): Promise<void> {
        const sessionIdentifier = this.state.lastSessionId || this.state.sessionId;
        if (!sessionIdentifier) {
            this.handleError(new Error('No previous session identifier available for reconnect.'), 'reconnect_failed');
            return;
        }

        this.clearDisconnectTimer();
        this._clearStartupTimer();
        this._startupAttemptId += 1;
        this._startupAttemptCount += 1;
        const currentAttemptId = this._startupAttemptId;
        this._startStreamStartupTimer('create_connection');
        this.setState({ isStreamStarting: true, status: StreamState.LOADING, transitionMode: 'connecting' });

        try {
            const signalRequest = await this.gameliftstreams?.generateSignalRequest();
            if (currentAttemptId !== this._startupAttemptId) {
                return;
            }

            const payload = {
                SessionIdentifier: sessionIdentifier,
                SignalRequest: signalRequest ?? '',
            };
            const data = (await this._streamApiPost('/reconnect', payload)) as { signalResponse: string };
            if (currentAttemptId !== this._startupAttemptId) {
                return;
            }

            try {
                const started = await this.startStream(data.signalResponse, currentAttemptId);
                if (!started) {
                    return;
                }
            } catch (startError) {
                if (currentAttemptId !== this._startupAttemptId) {
                    return;
                }

                this._clearStartupTimer();
                this.emitStartStreamFailed({
                    failureStage: 'start_stream',
                    errorCode: this.toErrorCode(startError),
                    errorMessage: this.toErrorMessage(startError)
                });
                this.handleError(startError, 'reconnect_failed');
                return;
            }

            this.setState({ sessionId: sessionIdentifier, lastSessionId: sessionIdentifier });
            this.emitAnalyticsEvent('start_stream_succeeded', {
                streamSessionId: sessionIdentifier,
                region: this.getPrimaryRegion(),
                reason: 'reconnect'
            });
        } catch (e) {
            if (currentAttemptId !== this._startupAttemptId) {
                return;
            }

            if (this._hasStartupTimeoutElapsed()) {
                this._onStartupTimeout('create_connection');
                return;
            }

            this._clearStartupTimer();
            this.emitStartStreamFailed({
                failureStage: 'create_connection',
                errorCode: this.toErrorCode(e),
                errorMessage: this.toErrorMessage(e)
            });
            this.handleError(e, 'reconnect_failed');
        }
    }

    /**
     * Polls for a stream session reaching ACTIVE. Termination is owned by
     * `_startupTimeoutTimer`, which bumps `_startupAttemptId` and exits this loop.
     */
    private async waitForACTIVE(arn: string, sg: string, attemptId: number): Promise<void> {
        while (true) {
            if (attemptId !== this._startupAttemptId) {
                return;
            }

            console.log(`Waiting for stream session: ${arn}`);
            try {
                const path = this._buildStreamSessionStatusPath(sg, arn);
                const data = (await this._streamApiGet(path)) as { status: string; signalResponse: string };
                if (attemptId !== this._startupAttemptId) {
                    return;
                }

                if (data.status === 'ACTIVE') {
                    try {
                        const started = await this.startStream(data.signalResponse, attemptId);
                        if (!started) {
                            return;
                        }
                    } catch (startError) {
                        if (attemptId !== this._startupAttemptId) {
                            return;
                        }

                        this._clearStartupTimer();
                        this.emitStartStreamFailed({
                            failureStage: 'start_stream',
                            errorCode: this.toErrorCode(startError),
                            errorMessage: this.toErrorMessage(startError)
                        });
                        this.handleError(startError);
                        return;
                    }

                    this.setState((prevState) => ({
                        ...prevState,
                        sessionId: arn,
                        lastSessionId: arn
                    }));

                    this.emitAnalyticsEvent('start_stream_succeeded', {
                        streamSessionId: arn,                        
                        region: this.getPrimaryRegion(),
                        reason: 'initial_start'
                    });
                    return;
                }
                await new Promise(resolve => setTimeout(resolve, 1000));
            } catch (e) {
                if (attemptId !== this._startupAttemptId) {
                    return;
                }

                if (this._hasStartupTimeoutElapsed()) {
                    this._onStartupTimeout('wait_for_active');
                    return;
                }

                this._clearStartupTimer();
                this.emitStartStreamFailed({
                    failureStage: 'wait_for_active',
                    errorCode: this.toErrorCode(e),
                    errorMessage: this.toErrorMessage(e)
                });
                this.handleError(e);
                this.setState({ isStreamStarting: false });
                return;
            }
        }
    }

    private async startStream(signalResponse: string, attemptId?: number): Promise<boolean> {
        await this.gameliftstreams?.processSignalResponse(signalResponse);
        if (attemptId !== undefined && attemptId !== this._startupAttemptId) {
            return false;
        }

        this.gameliftstreams?.attachInput();
        if (attemptId !== undefined && attemptId !== this._startupAttemptId) {
            return false;
        }

        this.dataChannelHandshakeStarted = false;
        this._clearStartupTimer();
        this.setState((prevState) => ({
            ...prevState,
            status: StreamState.RUNNING,
            isStreamStarting: false,
            transitionMode: 'none'
        }));

        if (this.urlConfig.hasControllerId) {
            this.setState({ showQrPanel: false });
        }

        return true;
    }

    private closeConnection(reason: string = 'user_action'): void {
        const streamSessionId = this.state.sessionId || this.state.lastSessionId;

        this.clearDisconnectTimer();
        this._clearStartupTimer();
        this._startupAttemptId += 1;
        this.dataChannelHandshakeService.stop();
        this.dataChannelHandshakeStarted = false;
        this.setState({ status: StreamState.STOPPED, inputEnabled: false, isStreamStarting: false, transitionMode: 'disconnected' });
        this.gameliftstreams?.close();
        this.resetGameLiftStreamsSDK();

        if (document.fullscreenElement) {
            document.exitFullscreen().then();
        }

        if (this.urlConfig.hasControllerId) {
            this.setState({ showQrPanel: true, mobileConnectionState: 'disconnected' });
        }

        if (streamSessionId) {
            this.emitAnalyticsEvent('stream_closed_successfully', {
                streamSessionId,                
                closeReason: reason,
                region: this.getPrimaryRegion()
            });
        }

        this.parentMessageRelayService.notifyCloseEvent(reason);
    }

    private scheduleDisconnectAutoTerminate(reason: string): void {
        if (this.disconnectTimer !== null) {
            return;
        }

        this.appendMobileLog(`Disconnect detected (${reason}); auto-terminate in ${this.autoTerminateTimeoutMs / 1000}s`);
        this.disconnectTimer = window.setTimeout(() => {
            this.disconnectTimer = null;
            if (this.state.status === StreamState.RUNNING) {
                this.closeConnection('disconnect_timeout');
            }
        }, this.autoTerminateTimeoutMs);
    }

    private clearDisconnectTimer(): void {
        if (this.disconnectTimer === null) {
            return;
        }

        window.clearTimeout(this.disconnectTimer);
        this.disconnectTimer = null;
    }

    private handleStreamConnectionState(state: string): void {
        if (state === 'connected') {
            this.clearDisconnectTimer();
            if (!this.dataChannelHandshakeStarted) {
                const shouldRelayGlQrUrl = !this.urlConfig.hasControllerId && Boolean(this.urlConfig.glQrURL);
                const initData = shouldRelayGlQrUrl ? { glQrURL: this.urlConfig.glQrURL } : undefined;
                this.dataChannelHandshakeService.start(
                    (payload: Uint8Array) => this.gameliftstreams?.sendApplicationMessage(payload) ?? false,
                    initData
                );
                this.dataChannelHandshakeStarted = true;
            }
            return;
        }

        if (state === 'disconnected') {
            this.dataChannelHandshakeService.stop();
            this.dataChannelHandshakeStarted = false;
            this.scheduleDisconnectAutoTerminate('stream_disconnected');
        }
    }

    private handleStreamChannelError(_error: unknown): void {
        this.scheduleDisconnectAutoTerminate('channel_error');
    }

    private handleServerDisconnect(_reasoncode: string): void {
        this.scheduleDisconnectAutoTerminate('server_disconnect');
    }

    private handleMobileMessage(message: MobileControllerMessage): void {
        if (message.type === 'key') {
            this.processMobileKey(message);
            return;
        }

        if (message.type === 'mouse') {
            this.processMobileMouse(message);
            return;
        }

        if (message.type === 'session_cleared' && message.reason === 'mobile_app_closed') {
            this.initializeMobileControllerSession();
            return;
        }

        if (message.type === 'mobile_status') {
            this.handleMobileStatus(message);
        }
    }

    private processMobileKey(message: MobileKeyMessage): void {
        if (this.state.status !== StreamState.RUNNING) {
            return;
        }

        const payloadCode = typeof message.payload.code === 'string' ? message.payload.code : '';
        const eventType = message.isDown ? 'keydown' : 'keyup';
        const payloadKeyCode = typeof message.payload.keyCode === 'number' ? message.payload.keyCode : undefined;

        if (eventType === 'keydown' && payloadKeyCode === 8) {
            this.closeConnection('shell_back_key');
            return;
        }

        if (eventType === 'keydown') {
            if (payloadCode && this.pressedKeys.has(payloadCode)) {
                return;
            }

            if (payloadCode) {
                this.pressedKeys.add(payloadCode);
            }
        }

        if (eventType === 'keyup' && payloadCode) {
            this.pressedKeys.delete(payloadCode);
        }

        const keyboardEvent = new KeyboardEvent(eventType, {
            key: typeof message.payload.key === 'string' ? message.payload.key : undefined,
            code: payloadCode || undefined,
            altKey: message.payload.altKey === true,
            ctrlKey: message.payload.ctrlKey === true,
            shiftKey: message.payload.shiftKey === true,
            metaKey: message.payload.metaKey === true,
            repeat: message.payload.repeat === true,
            bubbles: true
        });

        this.gameliftstreams?.processKeyboardEvent(keyboardEvent);
    }

    private processMobileMouse(message: MobileMouseMessage): void {
        if (this.state.status !== StreamState.RUNNING) {
            return;
        }

        const mouseEvent = new MouseEvent('mousemove', {
            clientX: message.x,
            clientY: message.y,
            bubbles: true
        });
        this.gameliftstreams?.processMouseEvent(mouseEvent);
    }

    private handleMobileStatus(message: MobileStatusMessage): void {
        if (message.connected) {
            this.setState({ mobileConnectionState: 'connected' });
            this.appendMobileLog(message.reconnected ? 'Mobile reconnected' : 'Mobile connected');
            this._clearMobileDisconnectGraceTimer();

            const controllerWSSessionId = this.state.mobileSessionId;
            if (controllerWSSessionId && !this.trackedQrScannedSessionIds.has(controllerWSSessionId)) {
                this.trackedQrScannedSessionIds.add(controllerWSSessionId);
                this.emitAnalyticsEvent('qr_code_scanned', {
                    streamSessionId: this.state.sessionId || this.state.lastSessionId,
                    controllerWSSessionId,
                    scanSignalSource: 'ws_connected'
                });
            }

            this._emitMobileStatusReconnectIfNeeded(message);

            if (!message.reconnected && this.autoStartOnMobileConnected && this.state.status === StreamState.STOPPED && !this.state.isStreamStarting) {
                void this.createStreamSession();
            }
            return;
        }

        this.setState({ mobileConnectionState: 'disconnected' });
        this.appendMobileLog('Mobile disconnected');
        this._scheduleMobileStatusDisconnect(message);
    }

    private _scheduleMobileStatusDisconnect(message: MobileStatusMessage): void {
        if (this._mobileDisconnectLatched) {
            return;
        }

        if (this._mobileDisconnectGraceTimer !== null) {
            return;
        }

        const controllerWSSessionId = this.state.mobileSessionId;
        if (!controllerWSSessionId) {
            return;
        }

        this._mobileDisconnectManual = typeof message.manualDisconnect === 'boolean' ? message.manualDisconnect : null;

        this._mobileDisconnectGraceTimer = window.setTimeout(() => {
            this._mobileDisconnectGraceTimer = null;
            if (this._mobileDisconnectLatched) {
                return;
            }

            this._mobileDisconnectLatched = true;
            const payload: Record<string, unknown> = {
                controllerWSSessionId,
                disconnectReason: 'mobile_status_disconnected'
            };

            if (this._mobileDisconnectManual !== null) {
                payload.manualDisconnect = this._mobileDisconnectManual;
            }

            this.emitAnalyticsEvent('mobile_controller_disconnected', payload);
            this._mobileDisconnectManual = null;
        }, this._mobileDisconnectGraceMs);
    }

    private _emitMobileStatusReconnectIfNeeded(message: MobileStatusMessage): void {
        if (message.reconnected !== true) {
            return;
        }

        this._mobileDisconnectLatched = false;

        const controllerWSSessionId = this.state.mobileSessionId;
        if (!controllerWSSessionId) {
            return;
        }

        this.emitAnalyticsEvent('mobile_controller_reconnected', {
            controllerWSSessionId
        });
    }

    private _clearMobileDisconnectGraceTimer(): void {
        if (this._mobileDisconnectGraceTimer === null) {
            return;
        }

        window.clearTimeout(this._mobileDisconnectGraceTimer);
        this._mobileDisconnectGraceTimer = null;
        this._mobileDisconnectManual = null;
    }
    private _handleErrorRetry(): void {
        const errorReason = this.state.errorReason;
        this._cleanupErrorState();
        this.emitAnalyticsEvent('stream_error_retry_selected', {
            errorReason,
            region: this.getPrimaryRegion()
        });
        this.setState({ errorReason: null, showQrPanel: false });
        void this.createStreamSession();
    }

    private _handleErrorReturn(): void {
        this._handleErrorExit('return_to_gameloop');
    }

    private _handleErrorExit(reason: string): void {
        const errorReason = this.state.errorReason;
        this._cleanupErrorState();
        this.emitAnalyticsEvent('stream_error_return_selected', {
            errorReason,
            closeReason: reason,
            region: this.getPrimaryRegion()
        });

        const closeDispatched = this.parentMessageRelayService.notifyCloseEvent(reason);
        if (!closeDispatched) {
            // Iframe relay unavailable — keep the error panel up so the user is
            // not stranded on a black screen.
            return;
        }

        this.setState({
            transitionMode: 'none',
            errorReason: null,
            status: StreamState.STOPPED,
            isStreamStarting: false,
            showQrPanel: false
        });
    }

    private _cleanupErrorState(): void {
        this._clearStartupTimer();
        this._startupAttemptId += 1;
        this.dataChannelHandshakeService.stop();
        this.dataChannelHandshakeStarted = false;
        this.gameliftstreams?.close();
        this.resetGameLiftStreamsSDK();
    }    

    private reconnectFromTransition = (): void => {
        if (this.state.lastSessionId) {
            void this.createStreamSessionConnection();
            return;
        }

        void this.createStreamSession();
    };

    private endSessionFromTransition = (): void => {
        this.setState({
            transitionMode: 'none',
            status: StreamState.STOPPED,
            isStreamStarting: false,
            showQrPanel: this.urlConfig.hasControllerId
        });
    };

    private _resolveErrorTitle(): string {
        const reason = this.state.errorReason;
        return reason ? ERROR_TITLE_BY_REASON[reason] : '';
    }

    render(): React.ReactElement {
        return (
            <>
                <ShellQrLinkPanel
                    visible={this.state.showQrPanel}
                    connectionStatus={this.state.mobileConnectionState}
                    sessionId={this.state.mobileSessionId}
                    mobileUrl={this.state.mobileUrl}
                    logs={this.state.mobileLogs}
                />

                <ShellTransitionPanel
                    mode={this.state.transitionMode}
                    errorTitle={this._resolveErrorTitle()}
                    onReconnect={this.reconnectFromTransition}
                    onEndSession={this.endSessionFromTransition}
                    onRetry={this._handleErrorRetry}
                    onReturnToGameLoop={this._handleErrorReturn}
                    lastSessionId={this.state.lastSessionId}
                />

                <div className="shell-stream-root" id="streamFullscreenContainer">
                    <div className="shell-stream-stage">
                        <video
                            id={'StreamVideoElement'}
                            autoPlay 
                            playsInline 
                            className="shell-stream-video"
                        />
                        {this.showStatsEnabled ? (
                            <StatsOverlay
                                gameliftstreams={this.gameliftstreams}
                                perfStats={this.state.perfStats}
                                isStreamRunning={this.state.status === StreamState.RUNNING}
                            />
                        ) : null}
                    </div>
                    <audio id={'StreamAudioElement'} autoPlay></audio>
                </div>
            </>
        );
    }
}

export default StreamComponent;

